import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAllPackage } from '../redux/slices/packageSlice'; // Adjust path to your package actions
import axios from 'axios';

const CreateFakeReview = () => {
    const dispatch = useDispatch();
    const { allPackage, loading: packagesLoading, error: packagesError } = useSelector((state) => state.package);

    const [formData, setFormData] = useState({
        packageId: '',
        tourType: '',
        rating: '',
        comment: '',
        user: {
            name: '',
            email: ''
        },
        media: []
    });
    const [mediaFiles, setMediaFiles] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        dispatch(fetchAllPackage());
    }, [dispatch]);

    // Auto-populate tourType based on selected package
    useEffect(() => {
        const selectedPackage = allPackage.find(pkg => pkg._id === formData.packageId);
        if (selectedPackage) {
            setFormData(prev => ({ ...prev, tourType: selectedPackage.tourType || '' }));
        }
    }, [formData.packageId, allPackage]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('user.')) {
            const field = name.split('.')[1];
            setFormData({
                ...formData,
                user: { ...formData.user, [field]: value }
            });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleMediaChange = (e) => {
        setMediaFiles([...e.target.files]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        // Client-side validation
        const wordCount = formData.comment.trim().split(/\s+/).length;
        if (wordCount < 10) {
            setError('Comment must be at least 10 words long.');
            setLoading(false);
            return;
        }

        if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
            setError('Rating must be between 1 and 5.');
            setLoading(false);
            return;
        }

        if (!formData.user.name || !formData.user.email) {
            setError('User name and email are required.');
            setLoading(false);
            return;
        }

        try {
            const formDataWithFile = new FormData();
            formDataWithFile.append('packageId', formData.packageId);
            formDataWithFile.append('tourType', formData.tourType);
            formDataWithFile.append('rating', formData.rating.toString());
            formDataWithFile.append('comment', formData.comment);
            formDataWithFile.append('user', JSON.stringify(formData.user));

            // Append media files and metadata
            const mediaMetadata = mediaFiles.map((file, index) => ({
                alt: `Media ${index + 1}`,
                type: file.type.startsWith('video/') ? 'video' : 'image'
            }));
            mediaFiles.forEach((file) => {
                formDataWithFile.append('reviewMedia', file);
            });
            if (mediaMetadata.length > 0) {
                formDataWithFile.append('media', JSON.stringify(mediaMetadata));
            }

            // Debug FormData contents
            console.log('FormData contents:');
            for (let pair of formDataWithFile.entries()) {
                console.log(`${pair[0]}: ${pair[1]}`);
            }

            const response = await axios.post(
                'https://api.prapanchapravasa.com/api3/v1/review/create-fake-review',
                formDataWithFile,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                        // Add Authorization header if required
                        // Authorization: `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            setSuccess('Fake review created successfully!');
            setFormData({
                packageId: '',
                tourType: '',
                rating: '',
                comment: '',
                user: { name: '', email: '' },
                media: []
            });
            setMediaFiles([]);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to create fake review';
            setError(errorMessage);
            console.error('Error details:', err.response?.data || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 p-6">
            <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-lg">
                <h1 className="text-2xl font-bold mb-6 text-gray-800">Create Fake Review</h1>
                {error && <p className="text-red-500 mb-4">{error}</p>}
                {success && <p className="text-green-500 mb-4">{success}</p>}
                {packagesLoading && <p className="text-blue-500 mb-4">Loading tour packages...</p>}
                {packagesError && <p className="text-red-500 mb-4">{packagesError}</p>}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Select Tour Package</label>
                        <select
                            name="packageId"
                            value={formData.packageId}
                            onChange={handleInputChange}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            required
                            disabled={packagesLoading}
                        >
                            <option value="">Select a tour package</option>
                            {allPackage.map((pkg) => (
                                <option key={pkg._id} value={pkg._id}>
                                    {pkg.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Tour Type</label>
                        <input
                            type="text"
                            name="tourType"
                            value={formData.tourType}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            required
                            readOnly
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Rating (1-5)</label>
                        <input
                            type="number"
                            name="rating"
                            value={formData.rating}
                            onChange={handleInputChange}
                            min="1"
                            max="5"
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Comment (min 10 words)</label>
                        <textarea
                            name="comment"
                            value={formData.comment}
                            onChange={handleInputChange}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                            rows="4"
                            required
                        ></textarea>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Fake User Details</label>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                name="user.name"
                                value={formData.user.name}
                                onChange={handleInputChange}
                                placeholder="Name"
                                className="p-2 border border-gray-300 rounded-md"
                                required
                            />
                            <input
                                type="email"
                                name="user.email"
                                value={formData.user.email}
                                onChange={handleInputChange}
                                placeholder="Email"
                                className="p-2 border border-gray-300 rounded-md"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Upload Media (Optional)</label>
                        <input
                            type="file"
                            multiple
                            onChange={handleMediaChange}
                            accept=".jpg,.jpeg,.png,.mp4"
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading || packagesLoading}
                        className={`w-full p-2 text-white rounded-md ${loading || packagesLoading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                    >
                        {loading ? 'Creating Review...' : 'Create Fake Review'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CreateFakeReview;